/**
 * interaction/facades/diagnostic.facade.js
 *
 * Sole business entry point for the diagnostic domain.
 * Bridges presentation → transport + diagnostic store.
 *
 * Rules: R4 (no DOM / state.js reads), R5 (single domain per PR).
 *
 * Methods:
 *   checkCompliance(listing)            → compliance API
 *   optimizeAlgorithm(listing)          → algorithm API
 *   evaluateScore(listing, kwLib)       → scoring API
 *   suggestRevision(ctx)                → revision API
 *   generateRevised(jobId, body, pollOpts)  → generateRevised + poll
 *
 * Usage (production):
 *   import { createDiagnosticFacade } from './facades/diagnostic.facade.js';
 *   const facade = createDiagnosticFacade({ ... });
 *
 * Usage (test — inject mocks):
 *   const facade = createDiagnosticFacade({ compliance: mockFn, ... });
 */

import { normalizeGenerationResult } from '../adapters/listing-draft.adapter.js';

// ── Factory ─────────────────────────────────────────────────────

export function createDiagnosticFacade(deps) {
  // Read deps dynamically to allow test overrides after creation
  const getDeps = () => deps;

  // ── Public API ──────────────────────────────────────────────

  /**
   * Run compliance check on listing.
   * Writes result to diagnostic store.
   */
  async function facadeCheckCompliance(listing, context = {}) {
    const hasContext = context && Object.keys(context).length > 0;
    const result = await getDeps().compliance(hasContext ? { listing, ...context } : listing);
    getDeps().diagnosticStore.patchDraft('complianceResult', result);
    return result;
  }

  /**
   * Run algorithm optimization tips.
   * Returns tips (not stored — consumed inline).
   */
  async function facadeOptimizeAlgorithm(listing, keywordLibrary) {
    const data = await getDeps().algorithmOptimize({ listing, keywordLibrary });
    return data.tips || data;
  }

  /**
   * Run listing quality scoring.
   * Writes result to diagnostic store.
   */
  async function facadeEvaluateScore(listing, keywordLibrary, context = {}) {
    const report = await getDeps().scoreEvaluate({ listing, keywordLibrary, ...context });
    getDeps().diagnosticStore.patchDraft('scoringReport', report);
    return report;
  }

  /**
   * Generate revision suggestions.
   * Writes suggestions + overview to diagnostic store.
   */
  async function facadeSuggestRevision(ctx) {
    const data = await getDeps().revisionSuggest(ctx);
    getDeps().diagnosticStore.patchDraft('revisionSuggestions', data.suggestions || []);
    getDeps().diagnosticStore.patchDraft('revisionOverview', data.overview || null);
    return data;
  }

  /**
   * Submit a revised generation job and poll for result.
   * @param {Object} [pollOpts] - forwarded to pollListingGeneration (e.g. onProgress)
   * @returns {Object} normalized generation result
   */
  async function facadeGenerateRevised(jobId, body, pollOpts = {}) {
    if (!jobId) throw new Error('generateRevised() requires a jobId');
    const start = await getDeps().generateRevised(jobId, body);
    if (typeof pollOpts.onSubmitted === 'function') pollOpts.onSubmitted(start);

    let data;
    try {
      if (start.taskId) {
        data = await getDeps().pollListingGeneration(start.taskId, pollOpts);
      } else {
        data = start;
      }
    } catch (error) {
      error.taskId = error.taskId || start.taskId || null;
      error.traceId = error.traceId || start.traceId || null;
      throw error;
    }

    const normalized = normalizeGenerationResult(data || {});
    normalized.task = {
      taskId: start.taskId || null,
      traceId: start.traceId || normalized.meta?.mergeMeta?.functionTrace?.traceId || null,
      batchJobId: start.batchJobId || jobId,
    };
    return normalized;
  }

  return {
    checkCompliance: facadeCheckCompliance,
    optimizeAlgorithm: facadeOptimizeAlgorithm,
    evaluateScore: facadeEvaluateScore,
    suggestRevision: facadeSuggestRevision,
    generateRevised: facadeGenerateRevised,
  };
}
